import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileExcel, faCheck, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './WalmartStockUploadPopup.css';

const WalmartStockUploadPopup = ({
  showWalmartStockPopup,
  setShowWalmartStockPopup,
  selectedFile,
  setSelectedFile,
  parsedData,
  setParsedData,
  uploadStatus,
  setUploadStatus,
  fileInputRef,
  handleFileUpload,
  handleUploadSubmit,
  closePopup
}) => {
  // Use provided closePopup or fallback
  const onClose = closePopup || (() => {
    setShowWalmartStockPopup(false);
    setSelectedFile(null);
    setParsedData([]);
    setUploadStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  });

  const handleFileInputClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  return (
    showWalmartStockPopup && (
      <div className="popup-overlay walmart-stock-popup-overlay">
        <div className="walmart-stock-popup-content">
          <div className="popup-header walmart-stock-popup-header">
            <h3>Upload Walmart Stock Data</h3>
            <button className="close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="popup-body">
            {/* Instructions */}
            <div className="upload-instructions">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Upload a CSV or Excel file (.csv, .xlsx, .xls)</li>
                <li>The file <strong>must</strong> contain these columns:</li>
                <ul>
                  <li><code>SKU</code> – product identifier</li>
                  <li><code>Available units</code> – current available stock</li>
                  <li><code>Inbound units</code> – inbound / incoming stock</li>
                </ul>
                <li>Data will be upserted (SKU unique). Existing SKUs are updated.</li>
              </ul>
            </div>

            {/* File selection */}
            <div className="file-selection">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
              />

              <div className="file-selector" onClick={handleFileInputClick}>
                <div className="file-icon">
                  <FontAwesomeIcon icon={faFileExcel} />
                </div>
                <div className="file-info">
                  {selectedFile ? (
                    <>
                      <div className="file-name">{selectedFile.name}</div>
                      <div className="file-size">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </div>
                    </>
                  ) : (
                    <div className="file-placeholder">
                      Click to select Excel/CSV file
                    </div>
                  )}
                </div>
              </div>

              <button className="btn-select-file" onClick={handleFileInputClick}>
                Browse Files
              </button>
            </div>

            {/* Data preview */}
            {parsedData.length > 0 && (
              <div className="data-preview">
                <h4>Data Preview ({parsedData.length} SKUs shown)</h4>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product Name</th>
                        <th>Available</th>
                        <th>Inbound</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((item, idx) => (
                        <tr key={idx}>
                          <td className="sku-cell">{item.sku}</td>
                          <td style={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                            {item.name || '—'}
                          </td>
                          <td className="number-cell">{item.available?.toLocaleString()}</td>
                          <td className="number-cell">{item.inbound?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {uploadStatus?.details && (
                  <p className="preview-more">{uploadStatus.details}</p>
                )}
              </div>
            )}

            {/* Status messages */}
            {uploadStatus && (
              <div className={`upload-status ${uploadStatus.type}`}>
                <div className="status-icon">
                  {uploadStatus.type === 'loading' && <FontAwesomeIcon icon={faSpinner} spin />}
                  {uploadStatus.type === 'success' && <FontAwesomeIcon icon={faCheck} />}
                  {uploadStatus.type === 'error' && <FontAwesomeIcon icon={faExclamationTriangle} />}
                </div>
                <div className="status-message">
                  {uploadStatus.message}
                  {uploadStatus.details && <span className="status-details"> {uploadStatus.details}</span>}
                </div>
              </div>
            )}
          </div>

          <div className="popup-footer">
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-upload"
              onClick={handleUploadSubmit}
              disabled={!selectedFile || parsedData.length === 0 || uploadStatus?.type === 'loading'}
            >
              {uploadStatus?.type === 'loading' ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                </>
              ) : (
                'Upload Stock Data'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default WalmartStockUploadPopup;